<template>
  <conformation-modal
    :visible="visible"
    :confirm-text="$t('pages.setting.confirm_email_change')"
    :cancel-text="$t('pages.setting.cancel')"
    :loading="changingEmail"
    @confirm="handleChangeEmail"
    @cancel="handleCancel"
  >
    <div class="mb-4">
      <h3 class="mb-4 text-xl font-bold text-gray-900 dark:text-slate-100">
        {{ $t("pages.setting.change_email") }}
      </h3>

      <div v-if="!requiresReauth">
        <p class="mb-3 text-sm text-gray-600 dark:text-slate-400">
          {{ $t("pages.setting.email_change_info") }}
        </p>
        <InformationText class="mb-4">
          {{ $t("pages.setting.email_change_warning") }}
        </InformationText>
        <TextInput
          v-model="newEmail"
          type="email"
          :placeholder="$t('pages.setting.new_email_placeholder')"
          @keyup.enter="handleChangeEmail"
        />
      </div>

      <div v-else>
        <div v-if="authStore.hasPasswordAuth()">
          <p class="mb-3 text-sm text-gray-600 dark:text-slate-400">
            {{ $t("pages.setting.reauth_required") }}
          </p>
          <div class="mb-3 rounded-lg bg-kekkle-yellow/20 p-3">
            <p class="text-sm font-medium text-gray-700 dark:text-slate-300">
              {{ $t("pages.setting.new_email") }}: {{ newEmail }}
            </p>
          </div>
          <input
            v-model="password"
            type="password"
            :placeholder="$t('pages.setting.enter_password')"
            class="w-full rounded-lg border-2 border-black-dark p-3 font-medium focus:ring-2 focus:ring-kekkle-blue focus:outline-hidden dark:bg-kekkle-dark-light dark:text-slate-200"
            @keyup.enter="handleChangeEmail"
          />
        </div>
        <div v-else>
          <p class="mb-3 text-sm text-gray-600 dark:text-slate-400">
            {{ $t("pages.setting.reauth_required_provider") }}
          </p>
          <div class="mb-3 rounded-lg bg-kekkle-yellow/20 p-3">
            <p class="text-sm font-medium text-gray-700 dark:text-slate-300">
              {{ $t("pages.setting.new_email") }}: {{ newEmail }}
            </p>
          </div>
          <p class="mb-4 text-sm text-gray-600 dark:text-slate-400">
            {{ $t("pages.setting.reauth_provider_instructions") }}
          </p>
          <default-button color="red" full-width @click="$emit('logout')">
            <span class="text-xl">🚪</span>
            {{ $t("global.inputs.labels.logout") }}
          </default-button>
        </div>
      </div>
    </div>
  </conformation-modal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import ConformationModal from "./ConformationModal.vue";
import InformationText from "../InformationText.vue";
import TextInput from "../Inputs/TextInput.vue";
import DefaultButton from "../Buttons/DefaultButton.vue";
import { setErrorToast, setSuccessToast } from "@/helpers/notifications/toast";
import * as Sentry from "@sentry/vue";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useI18n } from "vue-i18n";

type Props = {
  visible: boolean;
};

type Emits = {
  (e: "close"): void;
  (e: "logout"): void;
  (e: "success"): void;
};

defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
const authStore = useAuthStore();

const newEmail = ref("");
const changingEmail = ref(false);
const requiresReauth = ref(false);
const password = ref("");

async function handleChangeEmail() {
  if (!newEmail.value || !newEmail.value.trim()) {
    setErrorToast({ message: t("expectations.auth.invalid_email") });
    return;
  }

  // If reauth is required but no password provided
  if (requiresReauth.value && !password.value) {
    setErrorToast({
      message: t("pages.setting.password_required"),
    });
    return;
  }

  changingEmail.value = true;
  try {
    const result = await authStore.updateEmail(
      newEmail.value.trim(),
      requiresReauth.value ? password.value : undefined,
    );

    if (result.success) {
      setSuccessToast({
        message: t("pages.setting.email_changed_success"),
      });

      // Reset state
      newEmail.value = "";
      requiresReauth.value = false;
      password.value = "";

      emit("success");
      emit("close");
    } else if ("requiresReauth" in result && result.requiresReauth) {
      requiresReauth.value = true;
      password.value = "";
      setErrorToast({
        message: t("pages.setting.reauth_required"),
      });
    } else if ("error" in result) {
      Sentry.captureException(result.error);
      // Error toast already shown by handleAuthException
    }
  } catch (error: unknown) {
    Sentry.captureException(error);
  } finally {
    changingEmail.value = false;
  }
}

function handleCancel() {
  newEmail.value = "";
  requiresReauth.value = false;
  password.value = "";
  emit("close");
}
</script>
